/**
 * The stay, split into the calendar months it touches.
 *
 * Everything downstream is arithmetic on these segments, so this file is where a mistake would be
 * both invisible and expensive. Two rules from PRICING_RULES govern it:
 *
 *   P-10 — "The checkout date is neither occupied nor billable. Occupied nights end the night
 *           before."
 *   P-09 — "A calendar month fully covered by the stay is charged its full monthly rule price.
 *           February is not cheaper for having 28 days."
 *
 * Dates are handled as plain calendar dates in UTC. A stay has no time zone: a renter arriving on
 * the 12th arrives on the 12th wherever the server is, and using local time would make a quote's
 * total depend on where it was computed.
 */

const DAY_MS = 86_400_000

export function parseDate(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    throw new TypeError(`expected a YYYY-MM-DD date, got: ${iso}`)
  }
  const t = Date.parse(iso + 'T00:00:00Z')
  if (Number.isNaN(t)) throw new TypeError(`not a real date: ${iso}`)
  const d = new Date(t)
  // Rejects 2026-02-30, which Date.parse would otherwise roll forward to 2 March.
  if (d.toISOString().slice(0, 10) !== iso) throw new TypeError(`not a real date: ${iso}`)
  return d
}

export const daysInMonth = (year, month0) => new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate()

/** Nights between check-in and check-out. The checkout night is not occupied — P-10. */
export function nightsBetween(checkIn, checkOut) {
  const a = parseDate(checkIn)
  const b = parseDate(checkOut)
  const nights = Math.round((b - a) / DAY_MS)
  if (nights <= 0) throw new RangeError(`checkout must be after check-in: ${checkIn} → ${checkOut}`)
  return nights
}

/**
 * Split a stay into one segment per calendar month it touches.
 *
 * Each segment carries `isCompleteMonth`, which is what P-09 turns on: a month every one of whose
 * nights is occupied is charged in full, whatever its length.
 *
 * Returns: [{ year, month0, label, firstNight, lastNight, occupiedNights, daysInMonth, isCompleteMonth }]
 *
 * `firstNight` is carried because a season is a date range, not a month: to know which season a
 * month of the stay is priced in, something has to know which day that month of the stay begins.
 */
export function splitIntoMonths(checkIn, checkOut) {
  const start = parseDate(checkIn)
  const end = parseDate(checkOut) // exclusive: the checkout night is not occupied
  if (end <= start) throw new RangeError(`checkout must be after check-in: ${checkIn} → ${checkOut}`)

  const segments = []
  let cursor = new Date(start)

  while (cursor < end) {
    const year = cursor.getUTCFullYear()
    const month0 = cursor.getUTCMonth()
    const dim = daysInMonth(year, month0)

    // First night not in this month, or the end of the stay, whichever comes first.
    const nextMonth = new Date(Date.UTC(year, month0 + 1, 1))
    const segmentEnd = nextMonth < end ? nextMonth : end
    const occupiedNights = Math.round((segmentEnd - cursor) / DAY_MS)

    segments.push({
      year,
      month0,
      label: `${MONTHS[month0]} ${year}`,
      firstNight: cursor.toISOString().slice(0, 10),
      lastNight: new Date(segmentEnd - DAY_MS).toISOString().slice(0, 10),
      occupiedNights,
      daysInMonth: dim,
      // Complete means every night of the month is occupied — which is `dim` nights, starting on
      // the 1st. Not "occupiedNights >= 30", and not "the stay spans the month".
      isCompleteMonth: cursor.getUTCDate() === 1 && occupiedNights === dim,
    })

    cursor = segmentEnd
  }

  return segments
}

/**
 * How many WHOLE months the stay lasts — the number the length-of-stay tiers are read against.
 *
 * Counted by anniversary, the way a lease is written: 18 Sep → 22 Dec is three whole months and
 * four spare days, so it is 3.
 *
 * THIS IS NOT THE SAME AS THE NUMBER OF CALENDAR MONTHS THE STAY TOUCHES, and the difference is
 * money. A 35-night stay from 1 Oct to 5 Nov touches two calendar months but lasts one, and
 * counting it as two would hand it a two-month adjustment it did not earn — silently, on every
 * quote, on every home sharing the strategy.
 *
 * Month arithmetic clamps: 31 Jan plus one month is 28 Feb, not 3 March. Rolling forward would
 * make a stay ending 1 March "one month and one day" and a stay ending 28 February "not yet a
 * month", which is not how anybody counts.
 */
export function wholeMonthsBetween(checkIn, checkOut) {
  const a = parseDate(checkIn)
  const b = parseDate(checkOut)
  let n = 0
  while (addMonths(a, n + 1) <= b) n++
  return n
}

function addMonths(date, n) {
  const y = date.getUTCFullYear()
  const m = date.getUTCMonth()
  const d = date.getUTCDate()
  return new Date(Date.UTC(y, m + n, Math.min(d, daysInMonth(y, m + n))))
}

/** Does the stay cover at least one whole calendar month? P-02's second clause. */
export const coversACompleteMonth = (checkIn, checkOut) =>
  splitIntoMonths(checkIn, checkOut).some((s) => s.isCompleteMonth)

/**
 * Is the stay twelve calendar months or longer? P-08 — the annual fixed rate takes over.
 *
 * Measured in calendar months rather than nights: "12 months" to a renter and a landlord means
 * the same date next year, not 365 nights, and a lease is written the first way.
 */
export function isTwelveMonthsOrLonger(checkIn, checkOut) {
  const a = parseDate(checkIn)
  const b = parseDate(checkOut)
  const anniversary = new Date(Date.UTC(a.getUTCFullYear() + 1, a.getUTCMonth(), a.getUTCDate()))
  return b >= anniversary
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
